import React, { memo, MutableRefObject, useState } from 'react';
import { TextField, InputAdornment, IconButton } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import styled from 'styled-components';
import { FieldErrors, UseFormMethods } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d\w\W]{8,}$/;

interface PasswordTextFieldProps {
    id: string;
    name: string;
    label: string;
    errorMessage: string;
    passwordFieldsErrors: FieldErrors;
    register: UseFormMethods['register'];
    password?: MutableRefObject<Record<string, unknown>>;
    validateFormat?: boolean;
}

export const PasswordTextField = memo(({ 
    id, 
    name, 
    label, 
    errorMessage, 
    passwordFieldsErrors, 
    register, 
    password,
    validateFormat = true
}: PasswordTextFieldProps) => {
    const [visible, setVisible] = useState(false);
    const toggleVisibility = (e: React.FormEvent) => {
        e.preventDefault();
        setVisible(!visible);
    };
    const { t } = useTranslation();
    const translatedPasswordLengthRequirement = t('passwordLengthRequirement');
    const translatedPasswordFormatRequirement = t('passwordFormatRequirement');

    return (
        <StyledTextfield
            id={id}
            name={name}
            type={visible ? 'text' : 'password' }
            label={label}
            helperText={passwordFieldsErrors ? passwordFieldsErrors.message : null}
            size='small' 
            variant='outlined'
            fullWidth
            inputRef={register({
                required: {
                    value: true,
                    message: errorMessage
                },
                validate: (value) => {
                    if (validateFormat && name === 'password' && value.length < 8) {
                        return translatedPasswordLengthRequirement;
                    }
                    if (validateFormat && name === 'password' && !passwordRegex.test(value)) {
                        return translatedPasswordFormatRequirement;
                    } 
                    if (name === 'confirmPassword' && value !== password?.current) {
                        return errorMessage;
                    }
                }
            })}
            error={!!passwordFieldsErrors}
            InputProps = {
                {
                    endAdornment:
                        <InputAdornment position='end'>
                            <IconButton onClick={toggleVisibility}>
                                {
                                    visible ? <Visibility/> : <VisibilityOff />
                                }
                            </IconButton>
                        </InputAdornment>
                }
            }
        />
    );
});

const StyledTextfield = styled(TextField)`
    margin-bottom: 20px;

    .MuiFormLabel-root.Mui-error {
        color: #000000;
        opacity: .2;
    }

    .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline {
        border-color: #000000;
        opacity: .2;
    }
`;